import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { TFunction } from "i18next";
import {
  User,
  MapPin,
  Mail,
  Phone,
  Pencil,
  Hash,
  Ban,
  CircleCheckBig,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { setTitle } from "@/hooks/useHeader.ts";
import { updateUser, useUser } from "@/hooks/useUser.ts";
import { logout } from "@/api/common/interceptors/authenticationInterceptor.ts";
import { updateUser as updateUserApi } from "@/api/updateUser.ts";
import config from "@/config.ts";
import { updatePasscode } from "@/api/updatePasscode.ts";
import { Dialog, DialogTrigger } from "@/components/ui/dialog.tsx";
import EditEmailDialog from "@/components/EditEmailDialog.tsx";
import { Avatar, AvatarFallback } from "@/components/ui/avatar.tsx";
import { nameToInitials } from "@/lib/utils.ts";
import SwitchAccountDialogContent from "@/components/SwitchAccountDialogContent.tsx";
import { showErrorToast } from "@/lib/showErrorToast.tsx";
import { useTranslation } from "@/i18n";
import { useClient } from "@/hooks/useClient.ts";
import { getSignupQuestion } from "@/lib/signupQuestion.ts";
import SignupQuestionSelect from "@/components/SignupQuestionSelect.tsx";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";

const createPersonalInformationFormSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    address1: z
      .string()
      .min(5, {
        message: t("validation.addressMinLength"),
      })
      .optional(),
    address2: z.string().optional(),
    city: z
      .string()
      .min(2, {
        message: t("validation.cityMinLength"),
      })
      .optional(),
    county: z
      .string()
      .min(2, {
        message: t("validation.countyMinLength"),
      })
      .optional(),
    postcode: z
      .string()
      .min(5, {
        message: t("validation.postcodeMinLength"),
      })
      .optional(),
    country: z
      .string()
      .min(2, {
        message: t("validation.countryMinLength"),
      })
      .optional(),
    phoneNumber: z
      .string()
      .min(10, { message: t("validation.invalidPhoneNumber") }),
    // Not validated as required here: re-sending the saved value (even null)
    // is always accepted, and the API rejects clearing a required answer.
    signupAnswer: z.string(),
  });

type PersonalInformationFormValues = z.infer<
  ReturnType<typeof createPersonalInformationFormSchema>
>;

const createPasscodeFormSchema = (t: TFunction) =>
  z.object({
    passcode: z.string().min(2, {
      message: t("validation.minTwoCharacters"),
    }),
  });

type PasscodeFormValues = z.infer<ReturnType<typeof createPasscodeFormSchema>>;

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editEmailDialogIsOpen, setEditEmailDialogIsOpen] = useState(false);
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const user = useUser();
  const client = useClient();
  const signupQuestion = getSignupQuestion(client);
  const { t } = useTranslation();
  const personalInformationFormSchema = useMemo(
    () => createPersonalInformationFormSchema(t),
    [t],
  );
  const passcodeFormSchema = useMemo(() => createPasscodeFormSchema(t), [t]);

  if (!user) {
    throw new Error(
      "Something is wrong and protected routes aren't working properly.",
    );
  }

  useEffect(() => {
    setTitle(t("profile.title"));
  }, [t]);

  const personalInformationForm = useForm<PersonalInformationFormValues>({
    resolver: zodResolver(personalInformationFormSchema),
    defaultValues: {
      name: user.name || undefined,
      address1: user.address1 || undefined,
      address2: user.address2 || undefined,
      city: user.city || undefined,
      county: user.county || undefined,
      phoneNumber: user.phone_number || undefined,
      postcode: user.postcode || undefined,
      signupAnswer: user.signup_answer ?? "",
    },
    mode: "onChange",
  });

  const passcodeForm = useForm<PasscodeFormValues>({
    resolver: zodResolver(passcodeFormSchema),
    defaultValues: {
      passcode: user.ref_code || "",
    },
    mode: "onChange",
  });

  const onSubmitPersonalInformation = async (
    data: PersonalInformationFormValues,
  ) => {
    try {
      await updateUserApi({
        ...data,
        dateOfBirth: user.date_of_birth,
        // Leave the answer untouched when the client has no question
        signupAnswer: signupQuestion ? data.signupAnswer || null : undefined,
      });
      await updateUser();
      toast(t("profile.personalInformationUpdated"), {
        description: t("profile.personalInformationUpdatedDescription"),
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });
      setIsEditing(false);
    } catch (e: unknown) {
      const response = getErrorResponse<{ signup_answer?: string[] }>(e);
      if (response?.signup_answer) {
        personalInformationForm.setError("signupAnswer", {
          type: "server",
          message: response.signup_answer[0],
        });
        return;
      }
      showErrorToast(t("profile.personalInformationUpdateFailed"));
    }
  };

  const passCardName =
    config.passCardTerminology === "passCard"
      ? t("profile.passCard")
      : t("profile.membershipId");

  const onPasscodeSubmit = async (data: PasscodeFormValues) => {
    try {
      await updatePasscode(data);
      await updateUser();
      toast(t("profile.passcodeUpdated", { name: passCardName }), {
        description: t("profile.passcodeUpdatedDescription", {
          name: passCardName,
        }),
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });
      setIsEditing(false);
    } catch (e) {
      showErrorToast(t("profile.passcodeUpdateFailed"));
    }
  };

  const onCancel = () => {
    setIsEditing(false);
    personalInformationForm.reset();
    passcodeForm.reset();
  };

  const onEdit = () => {
    if (isEditing) {
      onCancel();
    } else {
      setIsEditing(true);
    }
  };

  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[320px_1fr]">
        <Card className="h-fit md:row-span-2">
          <CardHeader>
            <div className="flex flex-col items-center space-y-3">
              <Avatar className="h-24 w-24">
                <AvatarFallback>{nameToInitials(user.name)}</AvatarFallback>
              </Avatar>
              <div className="space-y-1 text-center">
                <h2 className="text-xl font-semibold">{user.name}</h2>
                <p className="text-muted-foreground text-sm">
                  {user.client_name}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="text-muted-foreground h-4 w-4" />
                <span className="text-sm">{user.email}</span>
                <Dialog
                  open={editEmailDialogIsOpen}
                  onOpenChange={setEditEmailDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="link" size="content">
                      {t("common.edit")}
                    </Button>
                  </DialogTrigger>
                  <EditEmailDialog
                    onClose={() => setEditEmailDialogIsOpen(false)}
                  />
                </Dialog>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="text-muted-foreground h-4 w-4" />
                <span className="text-sm">{user.phone_number}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-muted-foreground h-4 w-4" />
                <span className="text-sm">
                  {user.city}, {user.county}
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            {user.clients.length > 1 && (
              <Dialog
                open={switchDialogOpen}
                onOpenChange={setSwitchDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    {t("nav.switchClient")}
                  </Button>
                </DialogTrigger>
                <SwitchAccountDialogContent
                  onClose={() => setSwitchDialogOpen(false)}
                />
              </Dialog>
            )}

            <Button variant="destructive" className="w-full" onClick={logout}>
              {t("common.logout")}
            </Button>
          </CardFooter>
        </Card>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="mb-4 grid w-full grid-cols-2">
            <TabsTrigger value="personal">
              {t("profile.personalInfo")}
            </TabsTrigger>
            <TabsTrigger value="passcode">{passCardName}</TabsTrigger>
          </TabsList>
          <TabsContent value="personal">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t("profile.personalInformation")}
                  <Button
                    variant={isEditing ? "destructive" : "outline"}
                    onClick={onEdit}
                    className="ml-auto flex items-center gap-2"
                  >
                    {isEditing ? (
                      <Ban className="h-4 w-4" />
                    ) : (
                      <Pencil className="h-4 w-4" />
                    )}
                    {isEditing ? t("common.cancel") : t("common.edit")}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...personalInformationForm}>
                  <form
                    onSubmit={personalInformationForm.handleSubmit(
                      onSubmitPersonalInformation,
                    )}
                    className="space-y-4"
                  >
                    <FormField
                      control={personalInformationForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.name")}</FormLabel>
                          <FormControl>
                            {isEditing ? (
                              <Input {...field} disabled={!isEditing} />
                            ) : (
                              <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                {personalInformationForm.getValues("name")}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalInformationForm.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.phoneNumberLower")}</FormLabel>
                          <FormControl>
                            {isEditing ? (
                              <Input {...field} disabled={!isEditing} />
                            ) : (
                              <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                {personalInformationForm.getValues(
                                  "phoneNumber",
                                )}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalInformationForm.control}
                      name="address1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.address1")}</FormLabel>
                          <FormControl>
                            {isEditing ? (
                              <Input
                                placeholder={t("profile.addressPlaceholder")}
                                {...field}
                                disabled={!isEditing}
                              />
                            ) : (
                              <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                {personalInformationForm.getValues("address1")}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalInformationForm.control}
                      name="address2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.address2")}</FormLabel>
                          <FormControl>
                            {isEditing ? (
                              <Input
                                placeholder={t("profile.address2Placeholder")}
                                {...field}
                                disabled={!isEditing}
                              />
                            ) : (
                              <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                {personalInformationForm.getValues("address2")}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={personalInformationForm.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("form.city")}</FormLabel>
                            <FormControl>
                              {isEditing ? (
                                <Input
                                  placeholder={t("profile.cityPlaceholder")}
                                  {...field}
                                  disabled={!isEditing}
                                />
                              ) : (
                                <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                  {personalInformationForm.getValues("city")}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={personalInformationForm.control}
                        name="county"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("form.county")}</FormLabel>
                            <FormControl>
                              {isEditing ? (
                                <Input
                                  placeholder={t("profile.countyPlaceholder")}
                                  {...field}
                                  disabled={!isEditing}
                                />
                              ) : (
                                <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                  {personalInformationForm.getValues("county")}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={personalInformationForm.control}
                        name="postcode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("form.postalCode")}</FormLabel>
                            <FormControl>
                              {isEditing ? (
                                <Input
                                  placeholder={t("profile.postcodePlaceholder")}
                                  {...field}
                                  disabled={!isEditing}
                                />
                              ) : (
                                <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                  {personalInformationForm.getValues(
                                    "postcode",
                                  )}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    {signupQuestion && (
                      <FormField
                        control={personalInformationForm.control}
                        name="signupAnswer"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {signupQuestion.label}
                              {isEditing && signupQuestion.required && " *"}
                            </FormLabel>
                            {isEditing ? (
                              <SignupQuestionSelect
                                question={signupQuestion}
                                value={field.value}
                                onChange={field.onChange}
                                savedAnswer={user.signup_answer}
                              />
                            ) : (
                              <FormControl>
                                <div className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs md:text-sm">
                                  {personalInformationForm.getValues(
                                    "signupAnswer",
                                  )}
                                </div>
                              </FormControl>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {isEditing && (
                      <Button type="submit" className="w-full md:w-auto">
                        {t("profile.saveAddress")}
                      </Button>
                    )}
                  </form>
                </Form>
              </CardContent>
              <CardFooter />
            </Card>
          </TabsContent>
          <TabsContent value="passcode">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Hash className="h-5 w-5" />
                  {passCardName}
                  <Button
                    variant={isEditing ? "destructive" : "outline"}
                    onClick={onEdit}
                    className="ml-auto flex items-center gap-2"
                  >
                    {isEditing ? (
                      <Ban className="h-4 w-4" />
                    ) : (
                      <Pencil className="h-4 w-4" />
                    )}
                    {isEditing ? t("common.cancel") : t("common.edit")}
                  </Button>
                </CardTitle>
                <CardDescription>
                  {config.passCardTerminology === "passCard"
                    ? t("profile.passCardNote")
                    : t("profile.membershipIdNote")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...passcodeForm}>
                  <form
                    onSubmit={passcodeForm.handleSubmit(onPasscodeSubmit)}
                    className="space-y-4"
                  >
                    <FormField
                      control={passcodeForm.control}
                      name="passcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{passCardName}</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isEditing} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {isEditing && (
                      <Button type="submit" className="w-full md:w-auto">
                        {t("profile.updatePasscode", { name: passCardName })}
                      </Button>
                    )}
                  </form>
                </Form>
              </CardContent>
              <CardFooter />
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
